import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, KeyRound, Loader2 } from "lucide-react";
import { adminApi, authApi, apiError, formatDate, StatusBadge } from "./api";
import { useAdmin } from "./AdminContext";

const StatCard = ({ label, value, testId }) => (
  <div className="border border-ivory/10 bg-[#242424] p-6" data-testid={testId}>
    <p className="font-serif text-4xl text-ivory">{value}</p>
    <p className="mt-2 text-[0.7rem] uppercase tracking-[0.2em] text-ivory/50">{label}</p>
  </div>
);

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (form.new_password.length < 8) return setMsg({ ok: false, text: "New password must be at least 8 characters." });
    if (form.new_password !== form.confirm) return setMsg({ ok: false, text: "New passwords do not match." });
    setBusy(true);
    try {
      await authApi.post("/change-password", { current_password: form.current_password, new_password: form.new_password });
      setMsg({ ok: true, text: "Password updated successfully." });
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setMsg({ ok: false, text: apiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-ivory/20 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-ivory focus:outline-none";
  return (
    <div className="border border-ivory/10 bg-[#242424] p-8" data-testid="change-password-card">
      <p className="eyebrow flex items-center gap-2 text-ivory/60"><KeyRound className="h-3.5 w-3.5" /> My Account</p>
      <h3 className="mt-3 font-serif text-2xl text-ivory">Change my password</h3>
      <form onSubmit={submit} className="mt-5 space-y-4">
        <input type="password" placeholder="Current password" data-testid="cp-current" className={inputCls} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} autoComplete="current-password" />
        <input type="password" placeholder="New password (8+ characters)" data-testid="cp-new" className={inputCls} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} autoComplete="new-password" />
        <input type="password" placeholder="Confirm new password" data-testid="cp-confirm" className={inputCls} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} autoComplete="new-password" />
        {msg && <p className={`border-l-2 pl-3 text-sm ${msg.ok ? "border-emerald-400 text-ivory" : "border-brand text-ivory"}`} data-testid="cp-message">{msg.text}</p>}
        <button type="submit" disabled={busy} data-testid="cp-submit" className="flex items-center gap-2 bg-brand px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-ivory hover:bg-brand-dark disabled:opacity-60">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Update password
        </button>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAdmin();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    adminApi.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-ivory sm:text-4xl">Good day, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-2 text-sm text-ivory/60">Here's what's happening with the property listings.</p>
        </div>
        <Link to="/admin/listings/new" data-testid="add-new-property" className="flex items-center gap-2 bg-brand px-6 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-ivory hover:bg-brand-dark">
          <Plus className="h-5 w-5" /> Add New Property
        </Link>
      </div>

      {!stats ? (
        <div className="mt-10 flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-ivory/50" /></div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <StatCard label="Total properties" value={stats.total} testId="stat-total" />
            <StatCard label="Published" value={stats.published} testId="stat-published" />
            <StatCard label="Drafts" value={stats.drafts} testId="stat-drafts" />
            <StatCard label="Available" value={stats.available} testId="stat-available" />
            <StatCard label="Sold" value={stats.sold} testId="stat-sold" />
            <StatCard label="New enquiries" value={stats.new_enquiries} testId="stat-enquiries" />
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <div className="border border-ivory/10 bg-[#242424] p-8" data-testid="recent-listings">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl text-ivory">Recently updated</h2>
                <Link to="/admin/listings" className="text-xs font-semibold uppercase tracking-[0.15em] text-ivory/60 hover:text-ivory">View all</Link>
              </div>
              <ul className="mt-5 divide-y divide-ivory/10">
                {stats.recent_listings.map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-4 py-3.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ivory">{l.title}</p>
                      <p className="mt-0.5 text-xs text-ivory/50">{l.ref} · {l.district} · updated {formatDate(l.updated_at)}</p>
                    </div>
                    <StatusBadge status={l.workflow_status} />
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-8">
              <div className="border border-ivory/10 bg-[#242424] p-8" data-testid="recent-enquiries">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-2xl text-ivory">Latest enquiries</h2>
                  <Link to="/admin/enquiries" className="text-xs font-semibold uppercase tracking-[0.15em] text-ivory/60 hover:text-ivory">View all</Link>
                </div>
                <ul className="mt-5 divide-y divide-ivory/10">
                  {stats.recent_enquiries.length === 0 && <li className="py-4 text-sm text-ivory/50">No enquiries yet.</li>}
                  {stats.recent_enquiries.map((e) => (
                    <li key={e.id} className="py-3.5">
                      <p className="text-sm font-medium text-ivory">{e.name} <span className="text-ivory/40">· {formatDate(e.created_at)}</span></p>
                      <p className="mt-0.5 truncate text-xs text-ivory/50">{e.listing_title || e.kind} — {e.message}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <ChangePasswordCard />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
